import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import LoadingSection from "../LoadingSection";
import { createSupplierOrder, updateSupplierOrder, selectSupplierOrderDetails, selectSupplierOrderDetailsLoading, selectSupplierOrderCreateLoading, selectSupplierOrderCreateError, selectSupplierOrderUpdateLoading, selectSupplierOrderUpdateError } from "../../redux/supplierOrders/supplierOrdersSlice";
import { getSuppliersAll, selectSuppliersAll } from "../../redux/suppliers/suppliersSlice";
import { closeModal, setHasToRefresh } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";

const ORDER_STATUSES = [
    { id: 'bozza', label: 'Bozza' },
    { id: 'confermato', label: 'Confermato' },
    { id: 'in_consegna', label: 'In Consegna' },
    { id: 'consegnato', label: 'Consegnato' }
];

export default function AddEditSupplierOrder({ mode }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const orderDetails = useSelector(selectSupplierOrderDetails);
    const orderDetailsLoading = useSelector(selectSupplierOrderDetailsLoading);
    const createLoading = useSelector(selectSupplierOrderCreateLoading);
    const createError = useSelector(selectSupplierOrderCreateError);
    const updateLoading = useSelector(selectSupplierOrderUpdateLoading);
    const updateError = useSelector(selectSupplierOrderUpdateError);
    const suppliersAll = useSelector(selectSuppliersAll);

    const [formData, setFormData] = useState({
        supplierId: '',
        supplierLabel: '',
        supplierOrderDate: new Date().toISOString().split('T')[0],
        statusId: 'bozza',
        statusLabel: 'Bozza',
        deliveryDate: '',
        deliveryWeek: '',
        paymentTypeId: '',
        paymentTypeLabel: '',
        items: []
    });

    const [validation, setValidation] = useState({ supplierId: true });

    // Carica fornitori
    useEffect(() => {
        if (accountData?._id && (!suppliersAll || suppliersAll.length === 0)) {
            dispatch(getSuppliersAll({ accountId: accountData._id, token }));
        }
    }, [dispatch, accountData]);

    // Popola form in modalita' edit
    useEffect(() => {
        if (mode === 'edit' && orderDetails?._id) {
            setFormData({
                supplierId: orderDetails.supplierId || '',
                supplierLabel: orderDetails.supplierLabel || '',
                supplierOrderDate: orderDetails.supplierOrderDate ? new Date(orderDetails.supplierOrderDate).toISOString().split('T')[0] : '',
                statusId: orderDetails.statusId || 'bozza',
                statusLabel: orderDetails.statusLabel || 'Bozza',
                deliveryDate: orderDetails.deliveryDate ? new Date(orderDetails.deliveryDate).toISOString().split('T')[0] : '',
                deliveryWeek: orderDetails.deliveryWeek || '',
                paymentTypeId: orderDetails.paymentTypeId || '',
                paymentTypeLabel: orderDetails.paymentTypeLabel || '',
                items: orderDetails.items || []
            });
        }
    }, [mode, orderDetails]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name in validation) {
            setValidation(prev => ({ ...prev, [name]: true }));
        }
    };

    const handleSupplierChange = (e) => {
        const supplierId = e.target.value;
        const supplier = suppliersAll?.find(s => (s._id || s.id) === supplierId);
        setFormData(prev => ({
            ...prev,
            supplierId,
            supplierLabel: supplier?.company || ''
        }));
        setValidation(prev => ({ ...prev, supplierId: true }));
    };

    const handleStatusChange = (e) => {
        const statusId = e.target.value;
        const status = ORDER_STATUSES.find(s => s.id === statusId);
        setFormData(prev => ({
            ...prev,
            statusId,
            statusLabel: status?.label || ''
        }));
    };

    // Gestione items
    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                productId: '',
                productCode: '',
                productLabel: '',
                productSupplierCode: '',
                productBrandLabel: '',
                variantId: '',
                quantityTotal: 1,
                purchasePrice: 0,
                purchasePriceDiscount: 0,
                purchasePriceDiscounted: 0,
                sellPrice: 0,
                delivered: false
            }]
        }));
    };

    const updateItem = (index, field, value) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            newItems[index] = { ...newItems[index], [field]: value };

            // Auto-calcolo prezzo scontato
            if (field === 'purchasePrice' || field === 'purchasePriceDiscount') {
                const price = field === 'purchasePrice' ? parseFloat(value) || 0 : parseFloat(newItems[index].purchasePrice) || 0;
                const discount = field === 'purchasePriceDiscount' ? parseFloat(value) || 0 : parseFloat(newItems[index].purchasePriceDiscount) || 0;
                newItems[index].purchasePriceDiscounted = parseFloat((price - (price * discount / 100)).toFixed(2));
            }

            return { ...prev, items: newItems };
        });
    };

    const removeItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const validateForm = () => {
        const newValidation = {
            supplierId: !!formData.supplierId
        };
        setValidation(newValidation);
        return Object.values(newValidation).every(v => v);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const orderData = {
            ...formData,
            accountId: accountData._id,
            supplierOrderDate: formData.supplierOrderDate ? new Date(formData.supplierOrderDate) : new Date(),
            deliveryDate: formData.deliveryDate ? new Date(formData.deliveryDate) : null,
            creationDate: mode === 'add' ? new Date() : orderDetails?.creationDate
        };

        if (mode === 'add') {
            dispatch(createSupplierOrder({ supplierOrder: orderData, token }))
                .then((result) => {
                    if (!result.error) {
                        dispatch(closeModal());
                        dispatch(setHasToRefresh());
                    }
                });
        } else {
            dispatch(updateSupplierOrder({
                supplierOrder: { ...orderData, _id: orderDetails._id },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(closeModal());
                    dispatch(setHasToRefresh());
                }
            });
        }
    };

    if (mode === 'edit' && orderDetailsLoading) {
        return <LoadingSection />;
    }

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">
                {mode === 'add' ? 'Nuovo Ordine Fornitore' : 'Modifica Ordine Fornitore'}
            </h2>

            {(createLoading || updateLoading) && <LoadingSection isFixed />}

            <form onSubmit={handleSubmit}>
                {/* Dati Ordine */}
                <h3 className="text-lg font-semibold mb-3">Dati Ordine</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fornitore*</label>
                        <select
                            value={formData.supplierId}
                            onChange={handleSupplierChange}
                            className={`w-full p-2 border ${!validation.supplierId ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        >
                            <option value="">-- Seleziona Fornitore --</option>
                            {suppliersAll?.map(s => (
                                <option key={s._id || s.id} value={s._id || s.id}>{s.company}</option>
                            ))}
                        </select>
                        {!validation.supplierId && (
                            <p className="text-red-500 text-sm mt-1">Il fornitore è obbligatorio</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
                        <select
                            value={formData.statusId}
                            onChange={handleStatusChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {ORDER_STATUSES.map(s => (
                                <option key={s.id} value={s.id}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Data Ordine</label>
                        <input
                            type="date"
                            name="supplierOrderDate"
                            value={formData.supplierOrderDate}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Data Consegna Prevista</label>
                        <input
                            type="date"
                            name="deliveryDate"
                            value={formData.deliveryDate}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Settimana Consegna</label>
                        <input
                            type="text"
                            name="deliveryWeek"
                            value={formData.deliveryWeek}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="es. S22"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Pagamento</label>
                        <input
                            type="text"
                            name="paymentTypeLabel"
                            value={formData.paymentTypeLabel}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="es. Bonifico 30gg"
                        />
                    </div>
                </div>

                {/* Items */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold">Articoli Ordine</h3>
                        <button
                            type="button"
                            className="primary text-sm"
                            onClick={addItem}
                        >
                            <DynamicIcon iconClass="fa-solid fa-plus" />
                            Aggiungi Articolo
                        </button>
                    </div>

                    {formData.items.length === 0 && (
                        <div className="text-center text-gray-500 py-4 border border-dashed border-gray-300 rounded-md">
                            Nessun articolo aggiunto. Clicca "Aggiungi Articolo" per iniziare.
                        </div>
                    )}

                    {formData.items.map((item, index) => (
                        <div key={index} className="border border-gray-200 rounded-md p-4 mb-3 bg-gray-50">
                            <div className="flex justify-between items-center mb-3">
                                <span className="font-medium text-sm text-gray-700">Articolo #{index + 1}</span>
                                <button
                                    type="button"
                                    className="text-red-500 hover:text-red-700 text-sm"
                                    onClick={() => removeItem(index)}
                                >
                                    <DynamicIcon iconClass="fa-solid fa-trash" />
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Prodotto</label>
                                    <input
                                        type="text"
                                        value={item.productLabel}
                                        onChange={(e) => updateItem(index, 'productLabel', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Nome prodotto"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Codice Fornitore</label>
                                    <input
                                        type="text"
                                        value={item.productSupplierCode}
                                        onChange={(e) => updateItem(index, 'productSupplierCode', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Codice"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Marca</label>
                                    <input
                                        type="text"
                                        value={item.productBrandLabel}
                                        onChange={(e) => updateItem(index, 'productBrandLabel', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Marca"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Quantità</label>
                                    <input
                                        type="number"
                                        value={item.quantityTotal}
                                        onChange={(e) => updateItem(index, 'quantityTotal', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min="0"
                                        step="1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Prezzo Acquisto</label>
                                    <input
                                        type="number"
                                        value={item.purchasePrice}
                                        onChange={(e) => updateItem(index, 'purchasePrice', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Sconto %</label>
                                    <input
                                        type="number"
                                        value={item.purchasePriceDiscount}
                                        onChange={(e) => updateItem(index, 'purchasePriceDiscount', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Prezzo Scontato</label>
                                    <input
                                        type="number"
                                        value={item.purchasePriceDiscounted}
                                        readOnly
                                        className="w-full p-2 border border-gray-200 rounded-md text-sm bg-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Prezzo Vendita</label>
                                    <input
                                        type="number"
                                        value={item.sellPrice}
                                        onChange={(e) => updateItem(index, 'sellPrice', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                <div className="flex items-center pt-5">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={item.delivered || false}
                                            onChange={(e) => updateItem(index, 'delivered', e.target.checked)}
                                            className="rounded"
                                        />
                                        Consegnato
                                    </label>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Error message */}
                {(createError || updateError) && (
                    <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                        <p>{typeof (createError || updateError) === 'string' ? (createError || updateError) : 'Errore durante il salvataggio'}</p>
                    </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => dispatch(closeModal())}
                        disabled={createLoading || updateLoading}
                    >
                        Annulla
                    </button>
                    <button
                        type="submit"
                        className="primary"
                        disabled={createLoading || updateLoading}
                    >
                        {createLoading || updateLoading ? (
                            <>
                                <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <DynamicIcon iconClass="fas fa-save" />
                                {mode === 'add' ? 'Crea Ordine' : 'Aggiorna Ordine'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
